import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Check, Pause, Play, RotateCcw, SkipForward } from "lucide-react";
import type { GallerySnapshot } from "@roboscientist/contracts";
import { ResearchLineageMap } from "../../web/src/components/ResearchLineageMap.js";
import { ResearchLineageEvidencePanel } from "../../web/src/components/ResearchLineageEvidencePanel.js";
import { buildGalleryReplayPlan, easeGalleryReplayElapsed, projectGalleryReplay } from "./galleryReplay.js";

export function GalleryEvolutionReplay({ snapshot, presentationProgress }: { snapshot: GallerySnapshot; presentationProgress?: number }) {
  const plan = useMemo(() => buildGalleryReplayPlan(snapshot), [snapshot]);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [localElapsed, setElapsed] = useState(() => reducedMotion ? plan.duration : 0);
  const clockElapsed = presentationProgress == null ? localElapsed : Math.max(0, Math.min(1, presentationProgress)) * plan.duration;
  const elapsed = easeGalleryReplayElapsed(plan, clockElapsed);
  const [paused, setPaused] = useState(false);
  const [speed, setSpeed] = useState(8);
  const [take, setTake] = useState(0);
  const frame = useMemo(() => projectGalleryReplay(snapshot, plan, elapsed), [snapshot, plan, elapsed]);
  const playing = presentationProgress == null ? !paused && !frame.complete && !reducedMotion : !frame.complete;

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      setReducedMotion(query.matches);
      if (query.matches) setElapsed(plan.duration);
    };
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, [plan.duration]);
  useEffect(() => {
    if (!playing || presentationProgress != null) return;
    let previous = performance.now();
    let animationFrame: number;
    const tick = () => {
      const now = performance.now();
      const delta = Math.min(250, now - previous);
      previous = now;
      if (!document.hidden) setElapsed((value) => Math.min(plan.duration, value + delta * speed));
      animationFrame = window.requestAnimationFrame(tick);
    };
    animationFrame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [playing, plan.duration, speed, presentationProgress]);
  const restart = () => { setElapsed(0); setPaused(false); setTake((value) => value + 1); };
  const pause = () => setPaused(true);
  const progress = plan.duration ? clockElapsed / plan.duration : 1;

  return <div className={`proposal-evolution-view gallery-evolution-replay ${playing ? "is-playing" : "is-paused"}`}
    data-replay-state={frame.complete ? "complete" : playing ? "playing" : "paused"}
    data-replay-generation={frame.stage?.depth ?? 0}
    data-replay-visible={frame.visibleKeys.size}>
    <div className="gallery-replay-bar" aria-label="Research replay controls">
      <div className="gallery-replay-bar__title"><span><i /> RESEARCH REPLAY</span><strong>{frame.complete ? "Snapshot fully revealed" : `${paused ? "Paused" : "Exploring"} · Generation ${frame.stage?.depth ?? 0}`}</strong><small>Saved evidence · {frame.visibleKeys.size} / {snapshot.lineage.nodes.length} nodes</small></div>
      <div className="gallery-replay-bar__actions">
        <button type="button" onClick={() => frame.complete ? restart() : setPaused((value) => !value)} disabled={reducedMotion || !plan.duration} aria-label={playing ? "Pause research replay" : "Play research replay"}>{playing ? <Pause size={14} /> : <Play size={14} />}<span>{playing ? "Pause" : "Play"}</span></button>
        <select aria-label="Replay speed" value={speed} onChange={(event) => setSpeed(Number(event.target.value))} disabled={reducedMotion || !plan.duration}><option value={.5}>0.5×</option><option value={1}>1×</option><option value={2}>2×</option><option value={4}>4×</option><option value={8}>8×</option></select>
        <button type="button" onClick={restart} disabled={reducedMotion || !plan.duration}><RotateCcw size={14} /><span>Replay</span></button>
        <button type="button" onClick={() => setElapsed(plan.duration)} disabled={frame.complete}>{frame.complete ? <Check size={14} /> : <SkipForward size={14} />}<span>Show all</span></button>
      </div>
      <input className="gallery-replay-scrubber" type="range" aria-label="Research replay progress" aria-valuetext={`${Math.round(progress * 100)} percent of saved research revealed`} min={0} max={Math.max(1, plan.duration)} step={10} value={clockElapsed} onChange={(event) => { setElapsed(Number(event.target.value)); setPaused(true); }} disabled={reducedMotion || !plan.duration} style={{ "--replay-progress": `${progress * 100}%` } as CSSProperties} />
    </div>
    <ResearchLineageMap key={take} lineage={snapshot.lineage} readOnly replay={{
      instant: presentationProgress != null,
      canvasScale: presentationProgress == null ? undefined : 1.4,
      cameraProgress: presentationProgress,
      entryProgress: presentationProgress == null ? undefined : new Map([...plan.nodes].map(([key, timing]) => [key, Math.max(0, Math.min(1, (elapsed - timing.appear) / Math.max(1, plan.duration * .025)))])),
      visibleKeys: frame.visibleKeys,
      activeKeys: frame.activeKeys,
      nodeDetails: frame.nodeDetails,
      focusKey: frame.focusKey,
      depth: frame.stage?.depth ?? 0,
      stageProgress: frame.stageProgress,
      playing,
      complete: frame.complete,
      onInspect: pause
    }} />
    <ResearchLineageEvidencePanel lineage={frame.lineage} runs={frame.runs} replay={{ lineage: snapshot.lineage, runs: snapshot.runs, playing, complete: frame.complete }} />
  </div>;
}
